import {
  DbotEvent,
  DecisionContext,
  DecisionTypeHandler,
  DbotEventDispatcher,
} from "../common/commonDomain";
import { Incorporation, IncorporationForm } from "./incorporationDomain";
import { DecisionType } from "@/common/decisions";
import { toNumber } from "@/utils/numbers";
import { norway } from "@/utils/countries";
import { getFullName } from "@/utils/Roles";

export function initNewForm(
  dispatch: DbotEventDispatcher,
  context: DecisionContext<Incorporation>,
  data?: Incorporation,
): IncorporationForm {
  return new IncorporationForm(context, dispatch, data);
}

export function handleEvent(form: IncorporationForm, event: DbotEvent) {
  form.handleEvent(event);
}

export function isValid(form: IncorporationForm): boolean {
  return form.isValid;
}

export function validate(form: IncorporationForm): boolean {
  return form.validate();
}

export function applyForm(form: IncorporationForm): Incorporation {
  const existingShareTypes = form.data?.shareTypes ?? [];
  const shareNominalValue = Math.max(0, toNumber(form.shareNominalValueModel.parsedValue, 0));
  const numberOfShares = Math.max(0, Math.trunc(toNumber(form.numberOfSharesModel.parsedValue, 0)));
  const signer = form.signerModel.value;
  const customAddress = form.isCustomBusinessAddress ? form.selectedBusinessAddress : undefined;
  return {
    type: DecisionType.incorporation,
    numberOfCompanies: form.numberOfCompaniesModel.value,
    companyNames: form.companyNameModels.map((m) => m.validatedValue.trim()),
    industry: form.selectedNace?.label ?? "",
    industryCode: form.isCustomIndustry ? undefined : form.selectedNace?.code,
    aktivitetBransjeKode: form.isCustomIndustry
      ? undefined
      : form.selectedNace?.aktivitetBransjeKode,
    statutoryPurpose: (
      form.statutoryPurposeModel.validatedValue || form.statutoryPurposeModel.inputValue
    ).trim(),
    ownershipType: form.ownersModel.value,
    owners: [...(form.data?.owners ?? [])],
    shareNominalValue,
    shareTypes: [
      {
        sharetypeCode: form.shareClassModel.value,
        numberOfShares,
      },
      ...existingShareTypes.slice(1),
    ],
    signers: [
      ...(signer
        ? [{ type: signer.role, name: getFullName(signer), email: signer.email ?? "" }]
        : []),
      ...form.additionalSigners.map((s) =>
        s.kind === "role"
          ? {
              type: s.role.role,
              name: getFullName(s.role),
              email: s.role.email ?? "",
            }
          : {
              id: s.id,
              type: "other" as const,
              name: s.nameModel.validatedValue,
              email: s.emailModel.validatedValue,
              socialSecurityNumber: s.socialSecurityNumberModel.validatedValue,
              personReference: s.personReference,
            },
      ),
    ],
    addressIsSameAsParent: !form.isCustomBusinessAddress,
    address: customAddress?.addressText,
    zipCode: customAddress?.postalCode,
    city: customAddress?.city,
    addressObjType: customAddress?.objectType,
    country: customAddress ? norway : undefined,
    boardIsSameAsParent: !form.isOtherBoard,
    boardComposition: form.isOtherBoard ? form.boardCompositionPayload : undefined,
    isContinuation: form.continuationSelectModel.value,
    hasEmployees: form.hasEmployeesSelectModel.value,
    isNamingAuto: form.isNamingAuto,
    hasAuditor: form.hasAuditor,
    auditor: form.hasAuditor
      ? {
          company: {
            name: form.selectedAuditorCompany?.name,
            organisationNumber: form.selectedAuditorCompany?.organizationnumber,
            businessAddress1: form.selectedAuditorCompany?.address,
            businessZip: form.selectedAuditorCompany?.zip,
            businessCity: form.selectedAuditorCompany?.city,
            businessCountry: form.selectedAuditorCompany?.sourceCountry,
          },
          representativeName:
            form.auditorRepresentativeNameModel.validatedValue.trim() || undefined,
          email: form.auditorEmailModel.validatedValue || undefined,
          cost: form.auditorCostModel.parsedValue,
        }
      : undefined,
    hasAccountant: form.hasAccountant,
    accountant: form.hasAccountant
      ? {
          company: {
            name: form.selectedAccountantCompany?.name,
            organisationNumber: form.selectedAccountantCompany?.organizationnumber,
            businessAddress1: form.selectedAccountantCompany?.address,
            businessZip: form.selectedAccountantCompany?.zip,
            businessCity: form.selectedAccountantCompany?.city,
            businessCountry: form.selectedAccountantCompany?.sourceCountry,
          },
          representativeName:
            form.accountantRepresentativeNameModel.validatedValue.trim() || undefined,
          email: form.accountantEmailModel.validatedValue || undefined,
        }
      : undefined,
    deadlineForContribution: form.deadlineForContributionModel.validatedValue || undefined,
    notificationEmail: form.notificationEmailModel.validatedValue.trim() || undefined,
    invoiceEmail: form.invoiceEmailModel.validatedValue.trim() || undefined,
    notificationPhoneNumber: form.notificationPhoneModel.toValidPhoneNumber(),
    customArticles: form.articlesModel.customArticles,
    capitalConfirmer: form.selectedCapitalConfirmerCompany
      ? {
          company: {
            name: form.selectedCapitalConfirmerCompany.name,
            organisationNumber: form.selectedCapitalConfirmerCompany.organizationnumber,
            businessAddress1: form.selectedCapitalConfirmerCompany.address,
            businessZip: form.selectedCapitalConfirmerCompany.zip,
            businessCity: form.selectedCapitalConfirmerCompany.city,
            businessCountry: form.selectedCapitalConfirmerCompany.sourceCountry,
          },
          representativeName:
            form.capitalConfirmerRepresentativeNameModel.validatedValue.trim() || undefined,
          representativeEmail:
            form.capitalConfirmerRepresentativeEmailModel.validatedValue || undefined,
        }
      : undefined,
  };
}

export const incorporationHandler: DecisionTypeHandler<
  DecisionType.incorporation,
  Incorporation,
  IncorporationForm
> = {
  type: DecisionType.incorporation,
  initNewForm,
  handleEvent,
  isValid,
  validate,
  applyForm,
};
